import React, { useEffect, useState } from "react";
import { getAthleteCombines } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import LoadingSpinner from "../../../components/LoadingSpinner";
import moment from "moment/moment";
import EventResultsAthleteDashboard from "./EventResults";
import RegisterFlowModal from "./RegisterFlowModal";

function EventsAthleteDashboard() {
  const apiInstance = useAxiosInstance();
  const { user } = useAuthUser() || {};
  const [data, setData] = useState(undefined);
  const [fullData, setFullData] = useState(undefined);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [eventSelected, setEventSelected] = useState(undefined);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerEventId, setRegisterEventId] = useState(null);

  const [height, setHeight] = useState(window.innerHeight);
  const adjustedHeight = height - 210;

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      const results = await getAthleteCombines(apiInstance, user.id);
      setData(results);
      setFullData(results);
    };
    if (user?.id) fetchEvents();
  }, [apiInstance, user?.id]);

  const searchItem = (e) => {
    const q = e.target.value.toLowerCase().trim();

    if (!fullData) {
      setData([]);
      return;
    }

    const filtered = q
      ? fullData.filter(({ name, city, state }) =>
          [name, city, state].some((field) => field?.toLowerCase().includes(q)),
        )
      : fullData;

    setData(filtered);
  };

  const openResults = (eventId) => {
    setEventSelected(eventId);
    setIsResultsOpen(true);
  };

  const openRegisterFlow = (eventId) => {
    setRegisterEventId(eventId);
    setRegisterOpen(true);
  };

  return (
    <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300 h-full">
      <div className="grid gap-2 grid-cols-2 items-center">
        <div className="uppercase text-xl">Events</div>
        <input
          type="text"
          placeholder="Search Events..."
          className="border p-2 w-full rounded-md text-sm"
          onChange={searchItem}
        />
      </div>

      {data === undefined ? (
        <LoadingSpinner />
      ) : (
        <div
          className="overflow-y-auto mt-2 pl-4 pr-4 -m-4"
          style={{ height: `${adjustedHeight}px` }}
        >
          {data.length > 0 ? (
            data.map((item, index) => (
              <div
                className="bg-white flex flex-row border border-blue-700 rounded-md px-2 py-2 text-gray-800 items-center mb-3"
                key={index}
              >
                <div className="basis-1/4 text-base font-medium text-blue-700">
                  {moment(item.date).format("MM-DD-YYYY")}
                </div>

                <div className="basis-1/2 text-base pr-4">
                  <div className="flex flex-col w-40 sm:w-auto">
                    <p className="font-medium text-gray-800 uppercase truncate">
                      {item.name}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {item.city}
                      {item.state ? `, ${item.state}` : ""}
                    </p>
                  </div>
                </div>

                <div className="basis-1/4 justify-end flex">
                  {moment(item.date).isBefore(moment().startOf("day")) ? (
                    <button
                      className="bg-blue-700 text-white px-3 py-2 rounded-md uppercase text-xs hover:bg-blue-900"
                      onClick={() => openResults(item.id)}
                    >
                      Results
                    </button>
                  ) : item.registered ? (
                    <button
                      className="bg-gray-400 text-white px-4 py-2 rounded-md uppercase text-xs cursor-not-allowed"
                      disabled
                    >
                      Registered
                    </button>
                  ) : (
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded-md uppercase text-xs hover:bg-red-700"
                      onClick={() => openRegisterFlow(item.id)}
                    >
                      Register
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="mt-10 text-center text-md text-slate-600">
              <div className="w-full p-2 mt-5 flex flex-col items-center">
                <img
                  alt="No events that match your search"
                  src="/nodata.gif"
                  className="object-cover h-20 w-20 rounded-full border-gray-300 border"
                />
                <span className="mt-4">
                  There are no combines that match your search
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {isResultsOpen && (
        <EventResultsAthleteDashboard
          closeModal={() => setIsResultsOpen(false)}
          eventId={eventSelected}
          userId={user.id}
        />
      )}

      <RegisterFlowModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        athleteId={user?.id}
        athleteEmail={user?.email}
        eventId={registerEventId}
        origin="DashboardEvent"
      />
    </div>
  );
}

export default EventsAthleteDashboard;
